import React from 'react';
import { Tag } from 'primereact/tag';

/**
 * TicketThread — read-only conversation view.
 *
 * Used by:
 *   - AdminInbox (PR 8) — shows the full thread including internal notes
 *   - (also usable in reporter views — server filters internal notes
 *     before returning, so the same component renders both contexts)
 *
 * Props:
 *   - ticket: thread object from GET /api/support/tickets/{ref}
 *       expects { ticket_ref, category, status, note, created_at, replies[] }
 *
 * Renders:
 *   - Header row with ref + category + status pill
 *   - Original "ticket raised" card (note + when)
 *   - Stream of replies — reporter bubbles right (blue), admin bubbles
 *     left (white). Internal notes get a dashed amber border so admins
 *     can never confuse them with public replies.
 *   - Empty state when no replies yet
 *
 * Keep this component PURE — no state, no API calls. Parent owns the
 * data so it can refresh after a reply.
 */
const TicketThread = ({ ticket }) => {
  if (!ticket) return null;

  const replies = Array.isArray(ticket.replies) ? ticket.replies : [];

  return (
    <div className="flex flex-column gap-3">
      {/* Original ticket card */}
      <div
        className="p-3 border-round shadow-1"
        style={{ background: '#fff', border: '1px solid var(--surface-border)' }}
      >
        <div className="flex align-items-center gap-2 mb-2 flex-wrap">
          <span
            className="font-mono text-xs font-bold"
            style={{ color: 'var(--primary-700)' }}
          >
            {ticket.ticket_ref}
          </span>
          <Tag value={humanizeCategory(ticket.category)} severity="info" rounded />
          <StatusTag status={ticket.status} />
          {ticket.super_admin_is_owner && (
            <Tag value="Super admin owns" severity="warning" rounded icon="pi pi-shield" />
          )}
        </div>

        <div className="flex justify-content-between align-items-flex-start gap-2 flex-wrap mb-2">
          <div className="text-xs text-color-secondary font-semibold">
            Raised by {ticket.raised_by?.name || 'Unknown'}
          </div>
          <div className="text-xs text-color-secondary">
            {formatTimestamp(ticket.created_at)}
          </div>
        </div>

        <div className="text-sm text-color line-height-3" style={{ whiteSpace: 'pre-wrap' }}>
          {ticket.note || '(no note added)'}
        </div>

        {/* Attachments on the original ticket */}
        {Array.isArray(ticket.attachments) && ticket.attachments.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-3">
            {ticket.attachments.map((a) => (
              <AttachmentChip key={a.id} attachment={a} />
            ))}
          </div>
        )}
      </div>

      {/* Reply stream */}
      {replies.length === 0 ? (
        <div
          className="text-center text-sm text-color-secondary py-4 border-round"
          style={{ background: 'var(--surface-50)', border: '1px dashed var(--surface-border)' }}
        >
          <i
            className="pi pi-comments block mb-2"
            style={{ fontSize: '1.5rem', opacity: 0.5 }}
            aria-hidden="true"
          ></i>
          No replies yet. Be the first.
        </div>
      ) : (
        replies.map((r) => <ReplyBubble key={r.id} reply={r} />)
      )}
    </div>
  );
};

// ── Subcomponents ────────────────────────────────────────────────────

const ReplyBubble = ({ reply }) => {
  const fromReporter = reply.author_role === 'reporter';
  const isInternal = !!reply.is_internal;

  // Internal notes get their own visual treatment — center-aligned,
  // dashed amber border, "internal" badge.
  if (isInternal) {
    return (
      <div className="flex justify-content-center">
        <div
          className="p-3 border-round"
          style={{
            background: '#fffbeb',
            border: '1.5px dashed #fde68a',
            maxWidth: '85%',
          }}
        >
          <div
            className="flex align-items-center gap-2 mb-1 text-xs"
            style={{ color: '#b45309', fontWeight: 700 }}
          >
            <i className="pi pi-pencil" aria-hidden="true"></i>
            <span>INTERNAL NOTE · {reply.author_name || 'Admin'}</span>
            <span className="ml-auto" style={{ opacity: 0.7 }}>{formatTimestamp(reply.created_at)}</span>
          </div>
          <div className="text-sm text-color line-height-3" style={{ whiteSpace: 'pre-wrap' }}>
            {reply.message}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`flex ${fromReporter ? 'justify-content-end' : 'justify-content-start'}`}>
      <div
        className="p-3 border-round shadow-1"
        style={{
          maxWidth: '78%',
          background: fromReporter ? 'var(--primary-color)' : '#fff',
          color: fromReporter ? '#fff' : 'var(--text-color)',
          borderRadius: fromReporter ? '14px 14px 4px 14px' : '14px 14px 14px 4px',
        }}
      >
        <div
          className="flex align-items-center gap-2 mb-1 text-xs"
          style={{ opacity: 0.85 }}
        >
          <span className="font-bold">{reply.author_name || 'Support'}</span>
          <RoleBadge role={reply.author_role} fromReporter={fromReporter} />
        </div>
        <div className="text-sm line-height-3" style={{ whiteSpace: 'pre-wrap' }}>
          {reply.message}
        </div>
        <div className="text-xs mt-2" style={{ opacity: 0.7 }}>
          {formatTimestamp(reply.created_at)}
        </div>
      </div>
    </div>
  );
};

const RoleBadge = ({ role, fromReporter }) => {
  const label = humanizeRole(role);
  return (
    <span
      className="px-2 border-round-pill text-xs"
      style={{
        fontSize: '.6rem',
        fontWeight: 700,
        background: fromReporter ? 'rgba(255,255,255,.22)' : 'var(--surface-100)',
        color: fromReporter ? '#fff' : 'var(--text-color-secondary)',
        letterSpacing: '.05em',
        textTransform: 'uppercase',
      }}
    >
      {label}
    </span>
  );
};

const AttachmentChip = ({ attachment }) => (
  <div
    className="flex align-items-center gap-2 px-2 py-1 border-round"
    style={{
      background: 'var(--surface-100)',
      border: '1px solid var(--surface-border)',
      fontSize: '.75rem',
    }}
  >
    <i className="pi pi-paperclip" aria-hidden="true"></i>
    <span className="font-semibold">{attachment.original_filename}</span>
    <span className="text-color-secondary">{formatBytes(attachment.file_size_bytes)}</span>
  </div>
);

const StatusTag = ({ status }) => {
  const map = {
    open:        { value: 'Open',        severity: 'warning' },
    in_progress: { value: 'In Progress', severity: 'info' },
    resolved:    { value: 'Resolved',    severity: 'success' },
    closed:      { value: 'Closed',      severity: 'secondary' },
    reopened:    { value: 'Reopened',    severity: 'danger' },
  };
  const m = map[status] || { value: status, severity: 'info' };
  return <Tag value={m.value} severity={m.severity} rounded />;
};

// ── Helpers ──────────────────────────────────────────────────────────

const humanizeCategory = (key) => ({
  borrower_reports: 'Borrower Reports',
  offer_generation: 'Offer Generation',
  credits_issue:    'Credits Issue',
  others:           'Others',
}[key] || key);

const humanizeRole = (role) => ({
  reporter:     'Reporter',
  tenant_admin: 'Admin',
  super_admin:  'Super Admin',
}[role] || 'Support');

const formatTimestamp = (iso) => {
  if (!iso) return '';
  const then = new Date(iso);
  const now = new Date();
  const ms = now - then;
  const min = Math.floor(ms / 60_000);
  if (min < 1) return 'just now';
  if (min < 60) return `${min} min ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr} hr${hr > 1 ? 's' : ''} ago`;
  const day = Math.floor(hr / 24);
  if (day < 7) return `${day} day${day > 1 ? 's' : ''} ago`;
  return then.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: 'numeric', minute: '2-digit' });
};

const formatBytes = (bytes) => {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
};

export default TicketThread;
